// Pure helpers behind the Costs page (owner ruling of 2026-09-27: the AI call ledger, day by day, for
// administrators). DOM-free so they are unit-tested under node: the period and filter model and its
// URL round-trip, the queries the five routes take, the facet options drawn from the loaded rows, the
// chips, the sort values of the two client-sorted tables, and where a call's link goes. The page
// composes these; it decides no access — the server binds every read to the reader's scope and the
// audit section (permission-model P8/P9), and a meeting is linked only for its owner.
import type { AiCallDto, AiCallTotalsDto, CostsBreakdownRowDto, CostsDayDto } from '../../shared/api-types.js';
import {
  AI_CALL_KIND_LABELS,
  AI_CALL_KINDS,
  AI_CALL_OUTCOME_LABELS,
  AI_CALL_OUTCOMES,
  isOneOf,
  type AiCallFacet,
  type AiCallOutcome,
} from '../../shared/enums.js';
import { DELETED_ACCOUNT_WORDING } from '../../shared/text.js';
import type { Tone } from '../ui/StatusPill.js';
import { formatCount, formatDateOnly, formatUsd, parseDateOnly, pluralise } from '../ui/format.js';
import type { SortValue } from '../ui/sort-params.js';
import type { FacetOption, FilterChip } from './admin-helpers.js';

// --- Period and filters ---------------------------------------------------------------------------

/** The preset periods, in days, today included. */
export const PERIOD_PRESETS = ['7', '30', '90'] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];
export const DEFAULT_PERIOD: PeriodPreset = '30';

export interface CostFilters {
  /** A preset, or '' for the default — ignored when `from` or `to` is set (a custom period). */
  days: PeriodPreset | '';
  from: string;
  to: string;
  kind: string;
  model: string;
  person: string;
  outcome: string;
}

export const EMPTY_COST_FILTERS: Readonly<CostFilters> = Object.freeze({ days: '', from: '', to: '', kind: '', model: '', person: '', outcome: '' });

const COST_PARAM_KEYS = ['days', 'from', 'to', 'kind', 'model', 'person', 'outcome'] as const;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/u;

/** Reads the filters from the URL. Unknown presets, kinds and outcomes and malformed dates degrade to "no filter", and are named. */
export function parseCostFilters(params: URLSearchParams): { filters: CostFilters; dropped: string[] } {
  const dropped: string[] = [];
  const read = (key: (typeof COST_PARAM_KEYS)[number]): string => (params.get(key) ?? '').trim();
  const days = read('days');
  const from = read('from');
  const to = read('to');
  const kind = read('kind');
  const outcome = read('outcome');
  if (days !== '' && !isOneOf(PERIOD_PRESETS, days)) dropped.push('days');
  if (from !== '' && !parseDateOnly(from)) dropped.push('from');
  if (to !== '' && !parseDateOnly(to)) dropped.push('to');
  if (kind !== '' && !isOneOf(AI_CALL_KINDS, kind)) dropped.push('kind');
  if (outcome !== '' && !isOneOf(AI_CALL_OUTCOMES, outcome)) dropped.push('outcome');
  const keptFrom = dropped.includes('from') ? '' : from;
  const keptTo = dropped.includes('to') ? '' : to;
  return {
    filters: {
      // A custom period wins over a preset: the two never both apply.
      days: keptFrom !== '' || keptTo !== '' || dropped.includes('days') ? '' : (days as PeriodPreset | ''),
      from: keptFrom,
      to: keptTo,
      kind: dropped.includes('kind') ? '' : kind,
      model: read('model'),
      person: read('person'),
      outcome: dropped.includes('outcome') ? '' : outcome,
    },
    dropped,
  };
}

export function writeCostFilters(params: URLSearchParams, filters: CostFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of COST_PARAM_KEYS) {
    const value = filters[key].trim();
    // The default preset is written as an absence, so a pristine URL stays clean.
    if (value === '' || (key === 'days' && value === DEFAULT_PERIOD)) next.delete(key);
    else next.set(key, value);
  }
  return next;
}

export interface CostPeriod {
  /** Date-only bounds in the reader's zone, either possibly '' for an open end of a custom period. */
  from: string;
  to: string;
  /** The preset in force, or null for a custom period. */
  days: number | null;
}

/** Adds whole days to a `YYYY-MM-DD` date; a calendar operation, so no zone can shift it. */
export function shiftDateOnly(date: string, days: number): string {
  const parts = parseDateOnly(date);
  if (!parts) return date;
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return shifted.toISOString().slice(0, 10);
}

/** The period the filters describe: a preset counts back from today, today included; a custom period is its own bounds. */
export function periodOf(filters: CostFilters, today: string): CostPeriod {
  if (filters.from !== '' || filters.to !== '') return { from: filters.from, to: filters.to, days: null };
  const days = Number(filters.days === '' ? DEFAULT_PERIOD : filters.days);
  return { from: shiftDateOnly(today, -(days - 1)), to: today, days };
}

/** Minutes east of UTC for the reader's clock, the sign the API takes (`Date` reports minutes west). */
export function utcOffsetMinutes(now: Date = new Date()): number {
  return -now.getTimezoneOffset();
}

/** Everything except the period counts as an active filter, and so does any period other than the default preset. */
export function activeCostFilterCount(filters: CostFilters): number {
  let count = 0;
  for (const key of ['kind', 'model', 'person', 'outcome'] as const) if (filters[key].trim() !== '') count += 1;
  if (filters.from !== '' || filters.to !== '' || (filters.days !== '' && filters.days !== DEFAULT_PERIOD)) count += 1;
  return count;
}

/**
 * Whether one of the four filters narrows the period's calls — type, model, person, outcome. The period alone is not one
 * here: the summary line names it as its label, so its figures are the period's whole (04-data §1.11).
 */
export function costFilterNarrows(filters: CostFilters): boolean {
  return (['kind', 'model', 'person', 'outcome'] as const).some((key) => filters[key].trim() !== '');
}

export interface CostsQueryOptions {
  period: CostPeriod;
  utcOffset: number;
  sort?: { key: string; dir: 'asc' | 'desc' };
  page?: { limit: number; offset: number };
  facet?: string;
  table?: string;
}

/** The query the costs routes take for these filters; empty values are omitted (the API treats them the same). */
export function costsQuery(filters: CostFilters, options: CostsQueryOptions): Record<string, string | number | undefined> {
  return {
    utcOffset: options.utcOffset,
    from: options.period.from || undefined,
    to: options.period.to || undefined,
    kind: filters.kind || undefined,
    model: filters.model || undefined,
    person: filters.person || undefined,
    outcome: filters.outcome || undefined,
    facet: options.facet,
    table: options.table,
    sort: options.sort?.key,
    dir: options.sort?.dir,
    limit: options.page?.limit,
    offset: options.page?.offset,
  };
}

// --- Facets and chips -----------------------------------------------------------------------------

export interface CostFacets {
  kinds: FacetOption[];
  models: FacetOption[];
  people: FacetOption[];
  outcomes: FacetOption[];
}

/**
 * Kind and outcome are the closed enums; model and person are drawn from the loaded calls, so a control
 * never offers a value that matches nothing (R3). A value in the URL that no loaded row carries is kept,
 * labelled as such, so the reader can see and remove it rather than wonder why the tables are empty.
 */
export function costFacets(calls: readonly AiCallDto[], selected: CostFilters = EMPTY_COST_FILTERS): CostFacets {
  const models = new Set<string>();
  const people = new Map<string, string>();
  for (const call of calls) {
    models.add(call.model);
    if (call.person) people.set(call.person.id, call.person.displayName);
  }
  const byLabel = (a: FacetOption, b: FacetOption) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base', numeric: true });
  const modelOptions = [...models].map((value) => ({ value, label: value })).sort(byLabel);
  const peopleOptions = [...people].map(([value, label]) => ({ value, label })).sort(byLabel);
  if (selected.model !== '' && !models.has(selected.model)) modelOptions.push({ value: selected.model, label: `${selected.model} (none loaded)` });
  if (selected.person !== '' && !people.has(selected.person)) peopleOptions.push({ value: selected.person, label: `Unknown person (${selected.person.slice(0, 8)}…)` });
  return {
    kinds: AI_CALL_KINDS.map((kind) => ({ value: kind, label: AI_CALL_KIND_LABELS[kind] })),
    models: modelOptions,
    people: peopleOptions,
    outcomes: AI_CALL_OUTCOMES.map((outcome) => ({ value: outcome, label: AI_CALL_OUTCOME_LABELS[outcome] })),
  };
}

/** The period in words: "Last 30 days", or the custom bounds as dates. */
export function describePeriod(period: CostPeriod): string {
  if (period.days !== null) return `Last ${period.days} days`;
  if (period.from && period.to) return `${formatDateOnly(period.from)} – ${formatDateOnly(period.to)}`;
  if (period.from) return `From ${formatDateOnly(period.from)}`;
  if (period.to) return `Until ${formatDateOnly(period.to)}`;
  return 'All time';
}

/**
 * The filter bar's live line: what the figures count and over what (04-data §1.11), and — while a filter narrows the period
 * — both figures (§1.5: "214 of 2,453"): "Last 30 days: 12 matching calls · USD 1.10, of 214 calls · USD 3.20". Why a
 * labelled datum and these whole templates (05-content §8: "Sentences MUST NOT be assembled from concatenated fragments …
 * Use complete strings with named placeholders"; the Phase 1c audit's D29, G8 and D30 — the record's "Costs' tails"): the
 * line was "12 calls · USD 1.10 in the last 30 days (2 without a price)", a period phrase and a note spliced onto it, and
 * it named only the period while its figures covered the match. Each case is one template; the period is its label
 * (`describePeriod`), every count's words are chosen whole by `pluralise`, and calls without a price are a sentence of
 * their own. `unfiltered` is the server's `periodUnfiltered`; filtering with it missing says the whole is not known rather
 * than showing the match as though it were everything (§1.5).
 */
export function costsSummaryLine(totals: AiCallTotalsDto, unfiltered: AiCallTotalsDto | null, period: CostPeriod, filtering: boolean): string {
  const label = describePeriod(period);
  const cost = formatUsd(totals.costMicroUsd);
  let line: string;
  if (!filtering) {
    const calls = pluralise(totals.calls, { one: '# call', other: '# calls' });
    line = `${label}: ${calls} · ${cost}`;
  } else {
    const matching = pluralise(totals.calls, { one: '# matching call', other: '# matching calls' });
    if (unfiltered === null) {
      line = `${label}: ${matching} · ${cost}. The total without the filters is not known.`;
    } else {
      const all = pluralise(unfiltered.calls, { one: '# call', other: '# calls' });
      line = `${label}: ${matching} · ${cost}, of ${all} · ${formatUsd(unfiltered.costMicroUsd)}`;
    }
  }
  if (totals.unpricedCalls === 0) return line;
  const unpriced = filtering
    ? pluralise(totals.unpricedCalls, { one: 'One matching call has no price and is left out of the cost.', other: '# matching calls have no price and are left out of the cost.' })
    : pluralise(totals.unpricedCalls, { one: 'One call has no price and is left out of the cost.', other: '# calls have no price and are left out of the cost.' });
  // Why a full stop between them: two complete statements, never a clause spliced onto the line.
  return line.endsWith('.') ? `${line} ${unpriced}` : `${line}. ${unpriced}`;
}

/**
 * The filter bar's live line, chosen from the summary request's state. Why it asks whether the answer is the current
 * filters' own (`current`; the Phase 2a review of D29/G8): `useRequest` keeps the previous answer on screen while a new
 * filter's request runs, and keeps it when that request fails; labelling the old figures with the new filters said "214
 * matching calls … The total without the filters is not known." of the whole period (04-data §1.11: a total MUST say what
 * it counts and over what). So the figures show only under the filters they answered; while the current filters' answer
 * is on its way the line says so; and once the summary has failed it says nothing — the failure above owns the space, as
 * the queue's line does (`queueStatusLine`; app-baseline 05-client §5.4: a failed dependency never leaves an indefinite
 * loading state; D18).
 */
export function costsFilterLine(input: {
  status: 'loading' | 'ready' | 'error';
  refreshing: boolean;
  /** Whether the held answer is the current filters' own (`RequestState.current`). */
  current: boolean;
  totals: AiCallTotalsDto | null;
  unfiltered: AiCallTotalsDto | null;
  period: CostPeriod;
  narrowed: boolean;
}): string {
  const { status, refreshing, current, totals, unfiltered, period, narrowed } = input;
  if (status === 'error') return refreshing ? 'Loading the period…' : '';
  if (status === 'loading' || !current) return 'Loading the period…';
  return totals === null ? '' : costsSummaryLine(totals, unfiltered, period, narrowed);
}

/**
 * The calls table's footer: how many of the set are loaded — "Showing 100 of 214 matching calls" while a filter narrows it,
 * since the set is then the match (04-data §1.11), else "Showing 100 of 214 calls". Each a whole template (05-content §8).
 */
export function callsFooterLine(shown: number, total: number, filtering: boolean): string {
  const count = formatCount(shown);
  return filtering
    ? pluralise(total, { one: `Showing ${count} of # matching call`, other: `Showing ${count} of # matching calls` })
    : pluralise(total, { one: `Showing ${count} of # call`, other: `Showing ${count} of # calls` });
}

/** The active filters as chip text, for the always-visible rail (03-components §10). The default period is no chip. */
export function describeCostFilters(filters: CostFilters, facets: CostFacets, period: CostPeriod): FilterChip[] {
  const chips: FilterChip[] = [];
  const labelOf = (options: FacetOption[], value: string): string => options.find((option) => option.value === value)?.label ?? value;
  if (period.days === null || String(period.days) !== DEFAULT_PERIOD) chips.push({ key: 'period', label: `Period: ${describePeriod(period)}` });
  if (filters.kind !== '') chips.push({ key: 'kind', label: `Type: ${labelOf(facets.kinds, filters.kind)}` });
  if (filters.model !== '') chips.push({ key: 'model', label: `Model: ${labelOf(facets.models, filters.model)}` });
  if (filters.person !== '') chips.push({ key: 'person', label: `Person: ${labelOf(facets.people, filters.person)}` });
  if (filters.outcome !== '') chips.push({ key: 'outcome', label: `Outcome: ${labelOf(facets.outcomes, filters.outcome)}` });
  return chips;
}

// --- Cells ----------------------------------------------------------------------------------------

/** 05-content §6: finished well is success; an answer Calis refused is a warning; the provider's own failure, or none in time, is danger. */
export const AI_CALL_OUTCOME_TONES: Readonly<Record<AiCallOutcome, Tone>> = { ok: 'success', invalid_output: 'warning', error: 'danger', timeout: 'danger' };

/** The tokens a call used in all: the prompt (uncached and cached) and the answer; null when nothing was reported. */
export function tokensOf(call: Pick<AiCallDto, 'inputTokens' | 'outputTokens' | 'cacheReadTokens' | 'cacheCreationTokens'>): number | null {
  const counts = [call.inputTokens, call.outputTokens, call.cacheReadTokens, call.cacheCreationTokens];
  if (counts.every((count) => count === null)) return null;
  return counts.reduce<number>((sum, count) => sum + (count ?? 0), 0);
}

export type DaySortKey = 'day' | 'calls' | 'failures' | 'inputTokens' | 'outputTokens' | 'cost';

/** The raw value each by-day column sorts by; the table applies the type-aware, empties-last rule. */
export function daySortValue(row: CostsDayDto, key: DaySortKey): SortValue {
  switch (key) {
    case 'day':
      return row.day;
    case 'calls':
      return row.calls;
    case 'failures':
      return row.failures;
    case 'inputTokens':
      return promptTokensOf(row);
    case 'outputTokens':
      return row.outputTokens;
    case 'cost':
      return row.costMicroUsd;
    default:
      return null;
  }
}

/**
 * The whole prompt — uncached input, cache reads and cache writes together. The "Input tokens" cell
 * shows this sum, so the column sorts by it too; sorting on the uncached part alone would order the
 * rows against the figures on screen the moment prompt caching is used.
 */
export function promptTokensOf(totals: Pick<AiCallTotalsDto, 'inputTokens' | 'cacheReadTokens' | 'cacheCreationTokens'>): number {
  return totals.inputTokens + totals.cacheReadTokens + totals.cacheCreationTokens;
}

/**
 * The breakdown table's caption per grouping, each whole. Why not the tab's label lowercased into "Cost …" (05-content §1,
 * §8; the Phase 1c audit's D30): a label folded into a caption is a fragment, and lowercasing loses a proper noun's capital.
 */
export const COST_BREAKDOWN_CAPTIONS: Readonly<Record<AiCallFacet, string>> = { kind: 'Cost by type', model: 'Cost by model', person: 'Cost by person' };

/**
 * Said beside the by-person breakdown while it holds the line of calls for no one person (the owner's ruling Q17 of
 * escalation 20261005-200526: keep one combined line, with a note saying why): a breakdown groups calls by the person they
 * were for, and the ledger keeps no person for shared work or for a deleted account, so all of them share one line — while
 * the calls table names each deleted account by its pseudonym.
 */
export const COST_BREAKDOWN_NO_PERSON_NOTE =
  'Calls for shared work and for every deleted account share one line here: the breakdown groups calls by the person they were for, and Calis keeps no person for either. The calls table below names each deleted account by its pseudonym.';

/** Whether the by-person breakdown holds the line of calls for no one person, which the note above explains. */
export function breakdownHasNoPersonLine(facet: AiCallFacet, rows: readonly Pick<CostsBreakdownRowDto, 'key'>[]): boolean {
  return facet === 'person' && rows.some((row) => row.key === null);
}

export type BreakdownSortKey = 'label' | 'calls' | 'failures' | 'inputTokens' | 'outputTokens' | 'cost';

export function breakdownSortValue(row: CostsBreakdownRowDto, key: BreakdownSortKey): SortValue {
  switch (key) {
    case 'label':
      return row.label;
    case 'calls':
      return row.calls;
    case 'failures':
      return row.failures;
    case 'inputTokens':
      return promptTokensOf(row);
    case 'outputTokens':
      return row.outputTokens;
    case 'cost':
      return row.costMicroUsd;
    default:
      return null;
  }
}

// --- Links ----------------------------------------------------------------------------------------

/** The reasons no answer came whose row may name the network's cause in `errorMessage` (since 2026-10-02). */
const NETWORK_FAILURE_REASONS: ReadonlySet<string> = new Set(['connection', 'timeout']);

/**
 * The network's own account of a call no answer reached — its error code and sentence, as the ledger
 * keeps them in `errorMessage` ("UND_ERR_SOCKET — other side closed") — or null: the row was answered or
 * refused (its `errorMessage` is then the API's Reason), failed for another reason, or holds none.
 */
export function callNetworkCause(call: Pick<AiCallDto, 'httpStatus' | 'errorType' | 'errorMessage'>): string | null {
  if (call.httpStatus !== null || call.errorType === null || !NETWORK_FAILURE_REASONS.has(call.errorType)) return null;
  return call.errorMessage !== null && call.errorMessage !== '' ? call.errorMessage : null;
}

/**
 * What a failed call's Outcome line adds after its error code: the HTTP status and the API's error type
 * ("HTTP 400 · invalid_request_error"), or the reason no answer came ("timeout", "connection", "local") —
 * with the network's cause after it when the row kept one ("connection · ECONNRESET — read ECONNRESET");
 * null when the row holds neither — a completed call, an unusable answer, or a row written before the
 * ledger kept them (2026-09-28). Why: a refused call's status and type are what tell an administrator
 * a request Calis must fix from a service that was busy, and a failed connection's code tells a dropped
 * connection from DNS, routing or a certificate (docs/operations.md §12).
 */
export function callFailureLine(call: Pick<AiCallDto, 'httpStatus' | 'errorType' | 'errorMessage'>): string | null {
  const parts = [call.httpStatus === null ? null : `HTTP ${call.httpStatus}`, call.errorType, callNetworkCause(call)].filter(
    (part): part is string => part !== null && part !== '',
  );
  return parts.length === 0 ? null : parts.join(' · ');
}

export interface CallLink {
  to: string;
  label: string;
}

/**
 * Where a call's link goes. A meeting is visible to its owner alone (the data-layer ownership filter
 * has no superuser bypass), so the owner is sent to the meeting — its Slack tab for a check — and every
 * other reader to the meeting's audit trail, which the audit section already shows them by state and
 * identifier. A row with no meeting (older rows, the assistant's brief) has no link.
 */
export function callLink(call: Pick<AiCallDto, 'person' | 'meetingId' | 'kind'>, viewerId: string | null | undefined): CallLink | null {
  if (!call.meetingId) return null;
  const id = encodeURIComponent(call.meetingId);
  if (viewerId && call.person?.id === viewerId) {
    return call.kind === 'summary' ? { to: `/meetings/${id}`, label: 'Open meeting' } : { to: `/meetings/${id}?tab=slack`, label: 'Open check' };
  }
  return { to: `/admin/audit?targetType=meeting&target=${id}`, label: 'Audit trail' };
}

/**
 * What the Person column says of a call that names no one: the AI's look at a shared meeting's to-dos is
 * nobody's to be charged to (ADR 0014; its ledger row has no owner by design), and neither is the executives'
 * organisation report, made for nobody from many members' material (ADR 0019; design §2.9) — anything else lost its
 * person when their account was deleted before the row kept a name (migration 0051) — in the app's words
 * for an account that no longer resolves, the audit log's (src/shared/text.ts). An account deleted since
 * reads as its pseudonym instead, stored on the row ("Deleted account 1a2b3c4d").
 */
export function personlessLabel(kind: AiCallDto['kind']): string {
  if (kind === 'todo_link') return 'Shared: linking to-dos';
  if (kind === 'executive_report') return 'Shared: the executives’ report';
  return DELETED_ACCOUNT_WORDING;
}

/**
 * What the Person column shows — and so what the server sorts it by (design system 9.0.0 04-data §1.2; ADR 0167): the
 * name the row stored when the call was recorded; where it stored none, the personless wording, and the server sorts
 * those rows last in either direction. Why the person's name now where the row stored none but the key still resolves:
 * the database fills the copy for every row that names a living person (migration 0051's write trigger), so such a row
 * should not exist — and were one to, the page would sooner show the person's name than call a living person a deleted
 * account.
 */
export function callPersonName(call: Pick<AiCallDto, 'person' | 'personName' | 'kind'>): string {
  return call.personName ?? call.person?.displayName ?? personlessLabel(call.kind);
}

/**
 * The roster's name for the person now, where it differs from the name the row stored when the call was recorded; null
 * where they agree, or where no one resolves. Why it is said wherever the stored name is shown — beneath it in the Person
 * cell, on the table and on the card alike, and in the details: the filter and the by-person breakdown name people as
 * they are now while the column names them as they were, and 04-data §1.2 asks the surface to say so wherever the copy
 * and the live name can be read side by side; a header's hover title alone reaches neither a touch screen nor a card.
 */
export function callPersonNow(call: Pick<AiCallDto, 'person' | 'personName'>): string | null {
  if (!call.person || call.personName === null || call.person.displayName === call.personName) return null;
  return call.person.displayName;
}

/** The words for that difference, as the cell and the details both say it. */
export function nowNamedLine(name: string): string {
  return `now named ${name}`;
}

/**
 * The details' Person line: the stored name, the person's address now, and — where the roster names them differently
 * today — that name too, said as such (`callPersonNow`).
 */
export function callPersonLine(call: Pick<AiCallDto, 'person' | 'personName' | 'kind'>): string {
  const name = callPersonName(call);
  if (!call.person) return name;
  const now = callPersonNow(call);
  return `${name} · ${call.person.email}${now === null ? '' : ` · ${nowNamedLine(now)}`}`;
}
